import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  GitCompare,
  Plus,
  Trash2,
  ExternalLink,
  Play,
  Loader2,
  Download,
  TrendingUp,
  TrendingDown,
  Copy,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import ConfirmModal from "../components/ConfirmModal";
import {
  fetchScenarios,
  deleteScenario,
  createScenario,
  createStressTest,
  autoProject,
  runScenario,
  type ScenarioResponse,
} from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";
import { friendlyError } from "../utils/errors";
import EmptyState from "../components/EmptyState";
import { fmtCurrency, fmtCurrencyCompact, fmtPercent, fmtMultiple, fmtDecimal } from "../utils/format";
import { downloadCSV } from "../utils/csv";
import { CreateScenarioModal } from "./ScenarioModals";

export default function ScenariosPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const [scenarios, setScenarios] = useState<ScenarioResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoProjecting, setAutoProjecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [runningScenarioId, setRunningScenarioId] = useState<string | null>(null);
  const [stressTestVar, setStressTestVar] = useState("exit_cap_rate");
  const [stressTesting, setStressTesting] = useState(false);

  const reload = async () => {
    if (!dealId) return;
    try {
      const list = await fetchScenarios(dealId);
      setScenarios(list);
    } catch {
      /* ignore reload errors */
    }
  };

  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;

    async function init() {
      try {
        const list = await fetchScenarios(dealId!);
        if (cancelled) return;
        setScenarios(list);

        // Auto-generate Base Case if no scenarios exist
        if (list.length === 0) {
          setAutoProjecting(true);
          try {
            await autoProject(dealId!);
            const updated = await fetchScenarios(dealId!);
            if (!cancelled) setScenarios(updated);
          } catch (projErr) {
            // Auto-project failed (no parsed docs, or projection error) — show empty state
            console.warn("Auto-project failed:", projErr);
          } finally {
            if (!cancelled) {
              setAutoProjecting(false);
              setLoading(false);
            }
          }
        }
      } catch (e) {
        if (!cancelled)
          setError(friendlyError(e instanceof Error ? e.message : "Failed to load scenarios"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  async function handleDelete(scenarioId: string) {
    if (!dealId) return;
    await deleteScenario(dealId, scenarioId);
    setCompareIds((ids) => ids.filter((id) => id !== scenarioId));
    setConfirmDeleteId(null);
    await reload();
  }

  async function handleStressTest() {
    if (!dealId || !scenarios.length) return;
    const source = scenarios.find(s => s.projection_result) ?? scenarios[0];
    setStressTesting(true);
    try {
      await createStressTest(dealId, source.id, stressTestVar);
      await reload();
    } catch (e) {
      setError(friendlyError(e instanceof Error ? e.message : "Stress test failed"));
    } finally {
      setStressTesting(false);
    }
  }

  async function handleClone(scenario: ScenarioResponse) {
    if (!dealId) return;
    try {
      await createScenario(dealId, {
        name: `${scenario.name} (Copy)`,
        assumptions: scenario.assumptions ?? undefined,
      });
      await reload();
    } catch (e) {
      setError(friendlyError(e instanceof Error ? e.message : "Failed to clone scenario"));
    }
  }

  async function handleRunProjection(scenarioId: string) {
    if (!dealId) return;
    setRunningScenarioId(scenarioId);
    try {
      await runScenario(dealId, scenarioId);
      await reload();
    } catch (e) {
      setError(friendlyError(e instanceof Error ? e.message : "Failed to run projection"));
    } finally {
      setRunningScenarioId(null);
    }
  }

  function toggleCompare(id: string) {
    setCompareIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  if (loading || autoProjecting) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <LoadingSpinner />
        {autoProjecting && (
          <p className="text-sm text-bark-400 animate-pulse">
            Generating Base Case projection...
          </p>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <p className="text-red-700 font-medium mb-2">Failed to load scenarios</p>
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  const compareScenariosData = scenarios.filter(
    (s) => compareIds.includes(s.id) && s.projection_result
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-bark-800">
            Scenario Comparison
          </h2>
          <p className="text-sm text-bark-400 mt-1">
            Create alternative scenarios to compare assumptions and returns side by side. Edit assumptions on the Pro Forma page.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all shadow-md"
        >
          <Plus className="w-4 h-4" />
          New Scenario
        </button>
      </div>

      {scenarios.length === 0 ? (
        <EmptyState
          icon={GitCompare}
          title="No scenarios"
          description="Upload and parse a rent roll to auto-generate a Base Case, or create a scenario manually."
          action={
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all shadow-md"
            >
              <Plus className="w-4 h-4" />
              Create Scenario
            </button>
          }
        />
      ) : (
        <div className="bg-white rounded-xl border border-grove-300 divide-y divide-grove-200 shadow-sm">
          {scenarios.map((s) => {
            const hasResult = !!s.projection_result;
            const rm = s.projection_result?.return_metrics;
            const isConfirmingDelete = confirmDeleteId === s.id;
            return (
              <div key={s.id} className="px-6 py-5 flex items-center gap-4">
                <input
                  type="checkbox"
                  checked={compareIds.includes(s.id)}
                  onChange={() => toggleCompare(s.id)}
                  disabled={!hasResult}
                  className="w-4 h-4 rounded border-grove-300 bg-white text-forest-700 focus:ring-forest-500/30 disabled:opacity-20"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-bark-700">{s.name}</p>
                  {hasResult && rm && (
                    <>
                      <div className="flex gap-5 mt-2 text-xs text-bark-400">
                        <span>IRR <span className="text-bark-700 font-medium tabular-nums">{fmtPercent(rm.irr_unlevered, 2)}</span></span>
                        <span>EM <span className="text-bark-700 font-medium tabular-nums">{fmtMultiple(rm.equity_multiple)}</span></span>
                        <span>DSCR <span className="text-bark-700 font-medium tabular-nums">{rm.avg_dscr ? fmtDecimal(rm.avg_dscr) : "--"}</span></span>
                        <span>Cap <span className="text-bark-700 font-medium tabular-nums">{fmtPercent(rm.going_in_cap_rate, 2)}</span></span>
                        <span>Y1 NOI <span className="text-bark-700 font-medium tabular-nums">{fmtCurrency(s.projection_result?.annual_cash_flows?.[0]?.net_operating_income)}</span></span>
                        <span>Exit <span className="text-bark-700 font-medium tabular-nums">{fmtCurrency(rm.exit_price)}</span></span>
                      </div>
                      {/* Key assumptions summary */}
                      {s.assumptions && (
                        <div className="flex gap-4 mt-1.5 text-[10px] text-bark-300">
                          {s.assumptions.vacancy_rate_pct != null && <span>Vacancy {fmtPercent(s.assumptions.vacancy_rate_pct as number, 1)}</span>}
                          {s.assumptions.exit_cap_rate != null && <span>Exit {fmtPercent(s.assumptions.exit_cap_rate as number, 2)}</span>}
                          {s.assumptions.expense_growth_rate_pct != null && <span>OpEx Growth {fmtPercent(s.assumptions.expense_growth_rate_pct as number, 1)}</span>}
                          {s.assumptions.hold_period_years != null && <span>{String(s.assumptions.hold_period_years)}yr hold</span>}
                        </div>
                      )}
                    </>
                  )}
                  {!hasResult && (
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-bark-300">Not yet run</p>
                      <button
                        onClick={() => handleRunProjection(s.id)}
                        disabled={runningScenarioId === s.id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-forest-600 rounded-md hover:bg-forest-700 disabled:opacity-50 transition-colors"
                      >
                        {runningScenarioId === s.id ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Running...
                          </>
                        ) : (
                          <>
                            <Play className="w-3 h-3" />
                            Run Projection
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <Link
                    to={`/deals/${dealId}/cash-flow`}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Pro Forma
                  </Link>
                  <button
                    onClick={() => handleClone(s)}
                    className="p-2 text-bark-300 hover:text-forest-600 rounded-lg hover:bg-grove-50 transition-all"
                    title="Duplicate scenario"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(s.id)}
                    className="p-2 text-bark-300 hover:text-red-600 rounded-lg hover:bg-red-50 transition-all"
                    title="Delete scenario"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stress test card */}
      {scenarios.length > 0 && scenarios.length <= 2 && (
        <div className="bg-grove-50 border border-grove-300 rounded-xl p-6 mt-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-bark-700 mb-1">Quick Stress Test</p>
              <p className="text-xs text-bark-400">
                Generate Upside, Base, and Downside scenarios by varying a single assumption.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={stressTestVar}
                onChange={(e) => setStressTestVar(e.target.value)}
                className="px-3 py-2 text-xs border border-grove-300 rounded-lg bg-white text-bark-600 focus:outline-none focus:ring-2 focus:ring-forest-300"
              >
                <option value="exit_cap_rate">Exit Cap Rate</option>
                <option value="vacancy_rate_pct">Vacancy</option>
                <option value="market_rent_growth_pct_annual">Rent Growth</option>
                <option value="expense_growth_rate_pct">Expense Growth</option>
              </select>
              <button
                onClick={handleStressTest}
                disabled={stressTesting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-forest-700 text-white text-xs font-semibold rounded-lg hover:bg-forest-600 disabled:opacity-50 transition-all"
              >
                {stressTesting ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating...</>
                ) : (
                  <><GitCompare className="w-3.5 h-3.5" /> Run Stress Test</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {compareScenariosData.length >= 2 && (
        <ComparisonTable scenarios={compareScenariosData} />
      )}

      {showCreate && (
        <CreateScenarioModal
          dealId={dealId!}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            reload();
          }}
        />
      )}

      {confirmDeleteId && (
        <ConfirmModal
          title="Delete Scenario?"
          message={`Are you sure you want to delete "${scenarios.find(s => s.id === confirmDeleteId)?.name || "this scenario"}"? This cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => handleDelete(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
          danger
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comparison table
// ---------------------------------------------------------------------------

function getAssumption(s: ScenarioResponse, key: string): number | null {
  const val = s.assumptions?.[key];
  return typeof val === "number" ? val : null;
}

function ComparisonTable({ scenarios }: { scenarios: ScenarioResponse[] }) {
  const metrics: Array<{ label: string; get: (s: ScenarioResponse) => string }> = [
    { label: "Unlevered IRR", get: (s) => fmtPercent(s.projection_result?.return_metrics.irr_unlevered, 2) },
    { label: "Levered IRR", get: (s) => fmtPercent(s.projection_result?.return_metrics.irr_levered, 2) },
    { label: "Equity Multiple", get: (s) => fmtMultiple(s.projection_result?.return_metrics.equity_multiple) },
    { label: "Going-In Cap", get: (s) => fmtPercent(s.projection_result?.return_metrics.going_in_cap_rate, 2) },
    { label: "Exit Price", get: (s) => fmtCurrency(s.projection_result?.return_metrics.exit_price) },
    { label: "NPV", get: (s) => fmtCurrency(s.projection_result?.return_metrics.npv) },
    { label: "Avg DSCR", get: (s) => { const v = s.projection_result?.return_metrics.avg_dscr; return v ? fmtDecimal(v) : "--"; } },
    { label: "Year 1 NOI", get: (s) => fmtCurrency(s.projection_result?.annual_cash_flows[0]?.net_operating_income) },
    { label: "Year 1 CF After Debt", get: (s) => fmtCurrency(s.projection_result?.annual_cash_flows[0]?.cash_flow_after_debt) },
  ];

  const assumptions: Array<{ label: string; key: string; fmt: (v: number | null) => string }> = [
    { label: "General Vacancy %", key: "general_vacancy_pct", fmt: (v) => fmtPercent(v, 1) },
    { label: "Credit Loss %", key: "credit_loss_pct", fmt: (v) => fmtPercent(v, 1) },
    { label: "Expense Growth %", key: "expense_growth_rate_pct", fmt: (v) => fmtPercent(v, 1) },
    { label: "Market Rent Growth %", key: "market_rent_growth_pct", fmt: (v) => fmtPercent(v, 1) },
    { label: "Exit Cap Rate", key: "exit_cap_rate", fmt: (v) => fmtPercent(v, 2) },
    { label: "Discount Rate", key: "discount_rate", fmt: (v) => fmtPercent(v, 2) },
    { label: "Renewal Probability %", key: "renewal_probability_pct", fmt: (v) => fmtPercent(v, 1) },
    { label: "Hold Period (years)", key: "hold_period_years", fmt: (v) => v != null ? `${v}` : "--" },
    { label: "Loan Amount", key: "loan_amount", fmt: (v) => fmtCurrency(v) },
    { label: "Interest Rate %", key: "interest_rate", fmt: (v) => fmtPercent(v, 2) },
    { label: "Amortization (years)", key: "amortization_years", fmt: (v) => v != null ? `${v}` : "--" },
  ];

  const base = scenarios.find(s => s.name === "Base Case") || scenarios[0];

  // Diff summary for non-base scenarios
  const diffs = scenarios.filter(s => s.id !== base.id).map(s => {
    const changes: string[] = [];
    for (const a of assumptions) {
      const baseVal = getAssumption(base, a.key);
      const curVal = getAssumption(s, a.key);
      if (curVal != null && baseVal != null && curVal !== baseVal) {
        changes.push(`${a.label.replace(/ %$/, "")} ${a.fmt(curVal)} (vs ${a.fmt(baseVal)})`);
      }
    }
    return { name: s.name, changes };
  }).filter(d => d.changes.length > 0);

  function exportComparisonCSV() {
    const headers = ["", ...scenarios.map(s => s.name)];
    const rows: (string | number)[][] = [];
    for (const m of metrics) rows.push([m.label, ...scenarios.map(s => m.get(s))]);
    rows.push(["--- Key Assumptions ---", ...scenarios.map(() => "")]);
    for (const a of assumptions) rows.push([a.label, ...scenarios.map(s => a.fmt(getAssumption(s, a.key)))]);
    downloadCSV([headers, ...rows], "scenario_comparison.csv");
  }

  // Delta cards + NOI chart data
  const CHART_COLORS = ["#2D5016", "#C8860A", "#4A7C2E", "#8B6914", "#1a3a0d"];
  const holdPeriod = Math.max(...scenarios.map(s => s.projection_result?.annual_cash_flows?.length || 0));
  const chartData = Array.from({ length: holdPeriod }, (_, i) => {
    const point: Record<string, unknown> = { year: `Yr ${i + 1}` };
    for (const s of scenarios) {
      const cf = s.projection_result?.annual_cash_flows?.[i];
      if (cf) point[s.name] = cf.net_operating_income;
    }
    return point;
  });

  function fmtDelta(val: number | null | undefined, baseVal: number | null | undefined, fmt: string): { text: string; positive: boolean } {
    if (val == null || baseVal == null) return { text: "—", positive: true };
    const diff = val - baseVal;
    const positive = diff >= 0;
    if (fmt === "pct") return { text: `${positive ? "+" : ""}${(diff * 100).toFixed(1)}pp`, positive };
    if (fmt === "x") return { text: `${positive ? "+" : ""}${diff.toFixed(2)}x`, positive };
    if (fmt === "dollar") return { text: `${positive ? "+" : ""}${fmtCurrency(diff)}`, positive };
    return { text: String(diff), positive };
  }

  return (
    <div className="space-y-4">
      {/* Delta Summary Cards */}
      {scenarios.filter(s => s.id !== base.id).map(s => {
        const bm = base.projection_result?.return_metrics;
        const sm = s.projection_result?.return_metrics;
        if (!bm || !sm) return null;
        const deltas = [
          { label: "IRR", ...fmtDelta(sm.irr_unlevered, bm.irr_unlevered, "pct") },
          { label: "EM", ...fmtDelta(sm.equity_multiple, bm.equity_multiple, "x") },
          { label: "Exit Price", ...fmtDelta(sm.exit_price, bm.exit_price, "dollar") },
          { label: "NPV", ...fmtDelta(sm.npv, bm.npv, "dollar") },
        ];
        return (
          <div key={s.id}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-bark-400 mb-2">{s.name} vs {base.name}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {deltas.map(d => (
                <div key={d.label} className="bg-white rounded-xl border border-grove-300 px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-1.5">
                    {d.positive ? <TrendingUp className="w-3.5 h-3.5 text-forest-600" /> : <TrendingDown className="w-3.5 h-3.5 text-red-500" />}
                    <span className={`text-sm font-semibold ${d.positive ? "text-forest-700" : "text-red-600"}`}>{d.text}</span>
                  </div>
                  <p className="text-[10px] text-bark-400 mt-0.5">{d.label}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* NOI Comparison Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl border border-grove-300 p-5 shadow-sm">
          <h4 className="text-sm font-semibold text-bark-700 mb-4">Net Operating Income Comparison</h4>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#D4D0C8" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v: number) => fmtCurrencyCompact(v)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: unknown) => [fmtCurrency(Number(v)), ""]} />
              <Legend />
              {scenarios.map((s, i) => (
                <Line key={s.id} type="monotone" dataKey={s.name}
                  stroke={CHART_COLORS[i % CHART_COLORS.length]}
                  strokeWidth={i === 0 ? 2.5 : 1.5}
                  strokeDasharray={i === 0 ? undefined : "5 5"}
                  dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Diff summaries */}
      {diffs.map(d => (
        <div key={d.name} className="bg-grove-50 border border-grove-200 rounded-lg px-4 py-2 text-sm text-bark-600">
          <span className="font-semibold text-bark-700">{d.name}</span> differs from {base.name}: {d.changes.join(", ")}
        </div>
      ))}

      <div className="bg-white rounded-xl border border-grove-300 shadow-sm">
        <div className="px-6 py-5 border-b border-grove-200 flex items-center justify-between">
          <h3 className="font-display text-sm font-semibold text-bark-700">Scenario Comparison</h3>
          <button onClick={exportComparisonCSV} className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 transition-all">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th className="min-w-[180px]">Metric</th>
                {scenarios.map(s => <th key={s.id} className="num min-w-[140px]">{s.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {metrics.map(m => (
                <tr key={m.label}>
                  <td className="font-medium text-bark-600">{m.label}</td>
                  {scenarios.map(s => <td key={s.id} className="num">{m.get(s)}</td>)}
                </tr>
              ))}
              {/* Assumptions divider */}
              <tr><td colSpan={scenarios.length + 1} className="pt-4 pb-1"><span className="text-[10px] font-semibold uppercase tracking-wide text-bark-400">Key Assumptions</span></td></tr>
              {assumptions.map(a => {
                const baseVal = getAssumption(base, a.key);
                return (
                  <tr key={a.key}>
                    <td className="text-bark-500">{a.label}</td>
                    {scenarios.map(s => {
                      const val = getAssumption(s, a.key);
                      const differs = val != null && baseVal != null && val !== baseVal && s.id !== base.id;
                      return (
                        <td key={s.id} className={`num ${differs ? "bg-honey-50 font-semibold text-bark-700" : ""}`}>
                          {a.fmt(val)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Year-by-Year Annual Comparison */}
      {holdPeriod > 0 && (
        <div className="bg-white rounded-xl border border-grove-300 shadow-sm">
          <div className="px-6 py-4 border-b border-grove-200">
            <h3 className="font-display text-sm font-semibold text-bark-700">Annual Cash Flow Comparison</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full data-table text-[12px]">
              <thead>
                <tr>
                  <th className="min-w-[60px]">Year</th>
                  {scenarios.map(s => (
                    <th key={`noi-${s.id}`} className="num min-w-[110px]">NOI ({s.name})</th>
                  ))}
                  {scenarios.map(s => (
                    <th key={`cf-${s.id}`} className="num min-w-[110px]">CF After DS ({s.name})</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: holdPeriod }, (_, i) => {
                  const yr = i + 1;
                  return (
                    <tr key={yr}>
                      <td className="font-medium text-bark-600">Yr {yr}</td>
                      {scenarios.map(s => {
                        const cf = s.projection_result?.annual_cash_flows?.[i];
                        return <td key={`noi-${s.id}`} className="num tabular-nums">{cf ? fmtCurrency(cf.net_operating_income) : "—"}</td>;
                      })}
                      {scenarios.map(s => {
                        const cf = s.projection_result?.annual_cash_flows?.[i];
                        return <td key={`cf-${s.id}`} className="num tabular-nums">{cf ? fmtCurrency(cf.cash_flow_after_debt) : "—"}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
